import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, Briefcase, Car, FileText, Activity, Wrench, Wallet, ArrowRight, CheckCircle
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import PageSEO from '../components/PageSEO';
import StructuredData from '../components/StructuredData';

const DASHBOARD_IMAGE = "https://customer-assets.emergentagent.com/job_preview-suite/artifacts/xnqeg2qf_ChatGPT%20Image%20Mar%2029%2C%202026%2C%2012_57_41%20PM.png";

const SuitePage = () => {
  const { t } = useLanguage();

  const steps = [
    { number: 1, title: t('suite.step1Title'), description: t('suite.step1Desc') },
    { number: 2, title: t('suite.step2Title'), description: t('suite.step2Desc') },
    { number: 3, title: t('suite.step3Title'), description: t('suite.step3Desc') },
    { number: 4, title: t('suite.step4Title'), description: t('suite.step4Desc') },
    { number: 5, title: t('suite.step5Title'), description: t('suite.step5Desc') },
  ];

  const systemModules = [
    { name: t('suite.modDashboard'), icon: LayoutDashboard, description: t('suite.modDashboardDesc') },
    { name: t('suite.modJobs'), icon: Briefcase, description: t('suite.modJobsDesc') },
    { name: t('suite.modVehicles'), icon: Car, description: t('suite.modVehiclesDesc') },
    { name: t('suite.modFiles'), icon: FileText, description: t('suite.modFilesDesc') },
    { name: t('suite.modDiagnostics'), icon: Activity, description: t('suite.modDiagnosticsDesc') },
    { name: t('suite.modTools'), icon: Wrench, description: t('suite.modToolsDesc') },
    { name: t('suite.modWallet'), icon: Wallet, description: t('suite.modWalletDesc') },
  ];

  const benefits = [
    { title: t('suite.benefit1Title'), description: t('suite.benefit1Desc') },
    { title: t('suite.benefit2Title'), description: t('suite.benefit2Desc') },
    { title: t('suite.benefit3Title'), description: t('suite.benefit3Desc') },
    { title: t('suite.benefit4Title'), description: t('suite.benefit4Desc') },
  ];

  return (
    <div className="relative pt-20" data-testid="suite-page">
      <PageSEO path="/suite" />
      <StructuredData path="/suite" />
      <div className="ambient-glow ambient-orange" />
      <div className="ambient-glow ambient-blue" />
      <div className="fixed top-20 left-0 right-0 glow-line z-40" />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] to-[#080810]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="relative order-2 lg:order-1">
              <div className="relative">
                <img src={DASHBOARD_IMAGE} alt="RTEQ Portal Dashboard" className="w-full rounded-2xl shadow-2xl" />
                <div className="absolute -inset-4 bg-gradient-to-r from-[#FF5E00]/20 to-[#00D4FF]/20 rounded-3xl blur-2xl -z-10" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="order-1 lg:order-2">
              <p className="text-sm font-semibold text-[#00D4FF] tracking-widest uppercase mb-4">{t('suite.heroLabel')}</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                {t('suite.heroTitle1')}<br />
                <span className="gradient-text-orange">{t('suite.heroTitle2')}</span>
              </h1>
              <p className="text-zinc-400 text-base md:text-lg mb-8">{t('suite.heroDesc')}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://portal.rteqsolutions.com/register" target="_blank" rel="noopener noreferrer" className="glow-button px-8 py-4 text-base text-center" data-testid="suite-get-started-button">{t('suite.heroCta')}</a>
                <a href="https://portal.rteqsolutions.com" target="_blank" rel="noopener noreferrer" className="secondary-button px-8 py-4 text-base" data-testid="suite-access-button">{t('suite.heroAccess')}</a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-padding bg-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-sm font-semibold text-[#FF5E00] tracking-widest uppercase mb-4">{t('suite.workflowLabel')}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">{t('suite.workflowTitle')}</h2>
          </motion.div>

          {/* Desktop Steps */}
          <div className="hidden md:block relative mb-20">
            <div className="absolute top-6 left-0 right-0 h-0.5 step-connector" />
            <div className="grid grid-cols-5 gap-4 relative">
              {steps.map((step, index) => (
                <motion.div key={step.number} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center" data-testid={`step-${step.number}`}>
                  <div className="step-number mx-auto text-white text-lg mb-4 relative z-10">{step.number}</div>
                  <h4 className="font-heading font-bold text-white text-base mb-2">{step.title}</h4>
                  <p className="text-zinc-500 text-sm">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile Steps */}
          <div className="md:hidden space-y-4 mb-16">
            {steps.map((step, index) => (
              <motion.div key={step.number} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="flex items-center gap-4 glass-card rounded-xl p-4">
                <div className="step-number flex-shrink-0 text-white text-base">{step.number}</div>
                <div className="flex-1">
                  <h4 className="font-heading font-bold text-white text-sm">{step.title}</h4>
                  <p className="text-zinc-500 text-xs">{step.description}</p>
                </div>
                {index < steps.length - 1 && <ArrowRight className="w-4 h-4 text-[#FF5E00]" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Modules Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] via-[#080810] to-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-sm font-semibold text-[#00D4FF] tracking-widest uppercase mb-4">{t('suite.featuresLabel')}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">{t('suite.featuresTitle')}</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">{t('suite.featuresDesc')}</p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {systemModules.map((module, index) => (
              <motion.div key={module.name} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="glass-card rounded-xl p-6 group cursor-pointer" data-testid={`system-module-${index}`}>
                <module.icon className="w-10 h-10 mb-4 text-[#FF5E00] group-hover:text-[#00D4FF] transition-colors" />
                <h3 className="font-heading font-bold text-white text-lg mb-2">{module.name}</h3>
                <p className="text-zinc-500 text-sm">{module.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="section-padding bg-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-sm font-semibold text-[#FF5E00] tracking-widest uppercase mb-4">{t('suite.benefitsLabel')}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">{t('suite.benefitsTitle')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div key={benefit.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card rounded-2xl p-8 flex gap-4">
                <CheckCircle className="w-8 h-8 text-[#FF5E00] flex-shrink-0" />
                <div>
                  <h3 className="font-heading font-bold text-white text-xl mb-2">{benefit.title}</h3>
                  <p className="text-zinc-400">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-16 glass-card rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">{t('suite.ctaTitle')}</h3>
            <p className="text-zinc-400 mb-8">{t('suite.ctaDesc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://portal.rteqsolutions.com/register" target="_blank" rel="noopener noreferrer" className="glow-button px-8 py-4 text-base">{t('suite.ctaBtn')}</a>
              <Link to="/services" className="secondary-button px-8 py-4 text-base">{t('suite.ctaBtn2')}</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SuitePage;
